import { useCallback, useEffect, useRef, useState } from "react";
import { captureEvent } from "~/helpers/capture_posthog_event";

const getSpeechRecognition = () =>
  typeof window !== "undefined"
    ? window.SpeechRecognition || window.webkitSpeechRecognition
    : null;

const SILENCE_TIMEOUT_MS = 7000;

const useVoiceInput = ({ onTranscript, onStart, eventProps = {} } = {}) => {
  const [voiceState, setVoiceState] = useState("idle");
  const [errorMessage, setErrorMessage] = useState(null);

  const recognitionRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const startTimeRef = useRef(null);
  const finalTranscriptRef = useRef("");
  const manualStopRef = useRef(false);

  const isSupported = !!getSpeechRecognition();

  const clearSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  }, []);

  const stopRecognition = useCallback(() => {
    manualStopRef.current = true;
    clearSilenceTimer();
    if (recognitionRef.current) {
      recognitionRef.current.abort();
      recognitionRef.current = null;
    }
    setVoiceState("idle");
  }, [clearSilenceTimer]);

  const startSilenceTimer = useCallback(() => {
    clearSilenceTimer();
    silenceTimerRef.current = setTimeout(() => {
      manualStopRef.current = true;
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    }, SILENCE_TIMEOUT_MS);
  }, [clearSilenceTimer]);

  const resetSilenceTimer = useCallback(() => {
    clearSilenceTimer();
    startSilenceTimer();
  }, [clearSilenceTimer, startSilenceTimer]);

  const toggleVoiceInput = useCallback(() => {
    setErrorMessage(null);

    if (voiceState !== "idle") {
      stopRecognition();
      return;
    }

    const SpeechRecognitionAPI = getSpeechRecognition();
    if (!SpeechRecognitionAPI) {
      setErrorMessage("Voice typing is not supported in this browser.");
      return;
    }

    const recognition = new SpeechRecognitionAPI();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    if (onStart) {
      onStart();
    }
    finalTranscriptRef.current = "";
    startTimeRef.current = Date.now();
    manualStopRef.current = false;

    recognition.onstart = () => {
      setVoiceState("listening");
      startSilenceTimer();
    };

    recognition.onresult = (event) => {
      resetSilenceTimer();

      let interim = "";
      let finalText = "";

      for (let i = 0; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalText += result[0].transcript;
        } else {
          interim += result[0].transcript;
        }
      }

      if (finalText) {
        finalTranscriptRef.current = finalText;
      }

      if (onTranscript) {
        onTranscript(finalText + interim);
      }
    };

    recognition.onend = () => {
      clearSilenceTimer();

      // Browser can fire onend prematurely (e.g. brief silence in Chrome).
      // Auto-restart if the user didn't explicitly stop.
      if (!manualStopRef.current && recognitionRef.current) {
        try {
          recognitionRef.current.start();
          return;
        } catch {
          // Fall through to normal end handling
        }
      }

      const transcript = finalTranscriptRef.current;

      if (transcript && onTranscript) {
        onTranscript(transcript);

        const durationSeconds = startTimeRef.current
          ? Math.round((Date.now() - startTimeRef.current) / 1000)
          : 0;

        captureEvent("ai_assist_voice_input_used", {
          ...eventProps,
          duration_seconds: durationSeconds,
          transcription_length: transcript.length,
          browser: navigator.userAgent,
        });
      }

      recognitionRef.current = null;
      setVoiceState("idle");
    };

    recognition.onerror = (event) => {
      clearSilenceTimer();
      recognitionRef.current = null;
      setVoiceState("idle");

      if (
        event.error === "not-allowed" ||
        event.error === "permission-denied"
      ) {
        setErrorMessage("Microphone access required for voice typing.");
      } else if (event.error === "no-speech") {
        setErrorMessage("No speech detected. Please try again.");
      } else if (event.error !== "aborted") {
        setErrorMessage(`Voice input error: ${event.error}`);
      }
    };

    recognitionRef.current = recognition;

    try {
      recognition.start();
    } catch {
      setErrorMessage("Failed to start voice input. Please try again.");
      setVoiceState("idle");
      recognitionRef.current = null;
    }
  }, [
    voiceState,
    stopRecognition,
    startSilenceTimer,
    resetSilenceTimer,
    clearSilenceTimer,
    onTranscript,
    onStart,
  ]);

  useEffect(
    () => () => {
      stopRecognition();
    },
    [stopRecognition],
  );

  return {
    voiceState,
    isSupported,
    toggleVoiceInput,
    stopRecognition,
    errorMessage,
  };
};

export default useVoiceInput;
